/**
 * @tanstack/react-query hooks for the HR Core module (Phase 5.3). Thin
 * wrappers over lib/api/hr.ts so pages stay declarative and
 * caching/invalidation lives in one place.
 *
 * Query-key convention mirrors use-leave.ts / use-posh.ts:
 *   ["hr", <area>, ...params]   — flush whole module with ["hr"]
 *
 * The shared QueryClient (components/providers.tsx) sets retry: 1,
 * refetchOnWindowFocus: false, staleTime: 30s. The template + asset-type
 * + employee catalogues change rarely → longer staleTime; instance /
 * document / asset lists use the default.
 *
 * Onboarding-instance detail (GET /hr/onboarding/{id}) IS a real
 * endpoint (unlike leave, which derives detail from the list), so it
 * has its own query. A task complete/skip returns the recomputed
 * detail; we both seed that query and flush the instance list (the
 * %-complete / status on the list row moved too).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { hrApi } from "@/lib/api";
import { directoryKeys } from "@/hooks/use-directory";
import { payrollKeys } from "@/hooks/use-payroll";
import { invalidateHeadcount } from "@/lib/headcount-queries";
import { invalidatePayrollSetup } from "@/lib/payroll-setup-queries";
import { invalidateSalaryBreakup } from "@/lib/salary-queries";
import type {
  AssignAssetRequest,
  AssignOnboardingRequest,
  AssetTypeRequest,
  CreateAssetRequest,
  CreateEmployeeInput,
  OnboardingDetailResponse,
  TaskActionRequest,
  TemplateRequest,
  UpdateEmployeeInput,
} from "@/types/hr";

const CATALOGUE_STALE = 5 * 60 * 1000; // 5 min — slow-moving catalogues

/** GET /me/assets — Me → Assets and the Inbox task that asks for receipt. */
export const MY_ASSETS_KEY = ["me", "assets"] as const;

export const hrKeys = {
  all: ["hr"] as const,
  employees: () => [...hrKeys.all, "employees"] as const,
  accountStatus: (id: number) =>
    [...hrKeys.all, "employee-account-status", id] as const,
  templates: () => [...hrKeys.all, "templates"] as const,
  template: (id: number) => [...hrKeys.all, "template", id] as const,
  onboarding: (status?: string) =>
    [...hrKeys.all, "onboarding", status ?? "all"] as const,
  onboardingInstance: (id: number) =>
    [...hrKeys.all, "onboarding-instance", id] as const,
  documents: (userId?: number) =>
    [...hrKeys.all, "documents", userId ?? "self"] as const,
  assetTypes: () => [...hrKeys.all, "asset-types"] as const,
  assets: (status?: string, assignedTo?: number) =>
    [
      ...hrKeys.all,
      "assets",
      status ?? "all",
      assignedTo ?? "any",
    ] as const,
};

// ---------------------------------------------------------------------------
// Employee directory (HR-only server-side; query is allowed to 403 — the
// caller treats an error as "no directory" and degrades to id labels)
// ---------------------------------------------------------------------------

export function useEmployees(enabled = true) {
  return useQuery({
    queryKey: hrKeys.employees(),
    queryFn: ({ signal }) => hrApi.listEmployees(signal),
    staleTime: CATALOGUE_STALE,
    enabled,
    // A non-HR caller 403s here; don't spin retrying a hard authz fail.
    retry: false,
  });
}

/**
 * Create an employee (POST /employees). On success flush BOTH the
 * HR-only employee directory (hrKeys.employees — the assign/upload
 * pickers) AND the shared people directory (directoryKeys.all — backs
 * /dashboard/people for EVERY tenant user incl. plain employees) so the
 * new joiner shows up everywhere immediately. Mutating one without the
 * other would leave a stale directory on the People page (the brand-new
 * tenant's whole point). The mutation does NOT swallow errors — the
 * caller maps the backend's { error, field } envelope onto the form
 * (email_taken/validation→email, weak_password→password,
 * invalid_role→role) and toasts anything generic.
 */
export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEmployeeInput) => hrApi.createEmployee(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: hrKeys.employees() });
      // Flush every directory bucket (active / +inactive — keyed on the
      // includeInactive flag) so all callers refetch.
      void qc.invalidateQueries({ queryKey: directoryKeys.all });
      // A new active person: seat meter + setup checklist (F198).
      invalidateHeadcount(qc);
      invalidatePayrollSetup(qc);
    },
  });
}

/**
 * Update an employee (PATCH /employees/{id}). Same dual-flush as
 * useCreateEmployee — both the HR-only directory AND the shared
 * directory may reference the row (name / department / designation /
 * is_active changes affect both). G5.4 added the 6 payroll columns
 * which only the HR-only directory carries, but the cheaper coarse
 * invalidation is correct here.
 */
/** "Delete — added by mistake" (one or many). A dry run changes nothing. */
export function useDeleteMistakenEmployees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { ids: number[]; dryRun: boolean }) =>
      hrApi.deleteMistakenEmployees(vars.ids, vars.dryRun),
    onSuccess: (res) => {
      if (res.dry_run || res.deleted.length === 0) return;
      void qc.invalidateQueries({ queryKey: hrKeys.all });
      void qc.invalidateQueries({ queryKey: directoryKeys.all });
      invalidatePayrollSetup(qc);
      invalidateHeadcount(qc);
    },
  });
}

export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateEmployeeInput }) =>
      hrApi.updateEmployee(vars.id, vars.body),
    onSuccess: (updatedEmployee) => {
      // Directly write the PATCH response into the employees cache so the
      // edit form re-renders immediately with the new values without needing
      // a background GET round-trip. The PATCH response is the canonical
      // updated row returned by the backend after the write.
      //
      // We deliberately do NOT invalidate hrKeys.employees() here because
      // invalidateQueries would trigger a background GET /employees refetch
      // that races with the navigation and can serve the stale cache (before
      // the PATCH is reflected) on the re-mount — causing the edit form to
      // show empty salary even though setQueryData wrote the correct value.
      // The directory flush is still fired (name/dept/active changes affect
      // the people list column labels).
      qc.setQueryData(
        hrKeys.employees(),
        (old: { employees: import("@/types/hr").Employee[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            employees: old.employees.map((e) =>
              e.id === updatedEmployee.id ? updatedEmployee : e,
            ),
          };
        },
      );
      void qc.invalidateQueries({ queryKey: directoryKeys.all });
      // Role / active changes move the deactivation blockers + invite state.
      void qc.invalidateQueries({
        queryKey: hrKeys.accountStatus(updatedEmployee.id),
      });
      // Deactivate / reactivate moves the active headcount (F198); pay
      // fields move the setup checklist's pay-data coverage.
      invalidateHeadcount(qc);
      invalidatePayrollSetup(qc);
      // A salary change is recorded as a salary revision: the Pay tab's
      // salary history must show it without a reload (lifecycle
      // walkthrough 2026-10).
      void qc.invalidateQueries({
        queryKey: payrollKeys.salaryRevisions(updatedEmployee.id),
      });
      // Basic / HRA / Special move the yearly CTC line on People → Pay
      // (and My salary when HR edits their own row). Without this the
      // card showed the new gross next to last salary's CTC for up to
      // the 5-minute staleTime.
      invalidateSalaryBreakup(qc, updatedEmployee.id);
    },
  });
}

/**
 * Invite status + direct-deactivation eligibility for one employee
 * (GET /employees/{id}/account-status, HR-only). Drives "Resend invite"
 * and the Employment-status panel on the edit form.
 */
export function useEmployeeAccountStatus(id: number, enabled = true) {
  return useQuery({
    queryKey: hrKeys.accountStatus(id),
    queryFn: ({ signal }) => hrApi.getEmployeeAccountStatus(id, signal),
    enabled: enabled && id > 0,
    retry: false,
  });
}

/** POST /employees/{id}/resend-invite, then refresh the status. */
export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrApi.resendEmployeeInvite(id),
    onSettled: (_data, _err, id) => {
      void qc.invalidateQueries({ queryKey: hrKeys.accountStatus(id) });
    },
  });
}

/** GET /employees/{id}/probation — the probation chip (HR only). */
export function useEmployeeProbation(id: number, enabled = true) {
  return useQuery({
    queryKey: [...hrKeys.all, "employee-probation", id] as const,
    queryFn: ({ signal }) => hrApi.getEmployeeProbation(id, signal),
    enabled: enabled && id > 0,
    retry: false,
  });
}

/** POST /employees/{id}/confirm-probation, then refresh the chip + letters. */
export function useConfirmProbation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body?: { effective_date?: string; new_compensation_paise?: number };
    }) => hrApi.confirmEmployeeProbation(id, body),
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({
        queryKey: [...hrKeys.all, "employee-probation", vars.id],
      });
      // The letter library lives under the offboarding keys.
      void qc.invalidateQueries({ queryKey: ["offboarding"] });
    },
  });
}

const legacyProbationKey = [...hrKeys.all, "probation-legacy"] as const;

/** GET /employees/probation-legacy — the pre-tracking review list (HR only). */
export function useLegacyProbationReview(enabled = true) {
  return useQuery({
    queryKey: legacyProbationKey,
    queryFn: ({ signal }) => hrApi.getLegacyProbationReview(signal),
    enabled,
    retry: false,
  });
}

/** POST /employees/probation-legacy/reviewed — close the review. */
export function useMarkLegacyProbationReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => hrApi.markLegacyProbationReviewed(),
    onSuccess: (data) => {
      qc.setQueryData(legacyProbationKey, data);
    },
  });
}

/** POST /employees/{id}/probation/unconfirm — refresh the chip + the list. */
export function useUnconfirmLegacyProbation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrApi.unconfirmLegacyProbation(id),
    onSettled: (_data, _err, id) => {
      void qc.invalidateQueries({
        queryKey: [...hrKeys.all, "employee-probation", id],
      });
      void qc.invalidateQueries({ queryKey: legacyProbationKey });
    },
  });
}

/** POST /employees/{id}/correct-email — refresh everything keyed on the email. */
export function useCorrectEmployeeEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, email }: { id: number; email: string }) =>
      hrApi.correctEmployeeEmail(id, email),
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: hrKeys.accountStatus(vars.id) });
      void qc.invalidateQueries({ queryKey: hrKeys.employees() });
      void qc.invalidateQueries({ queryKey: directoryKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Onboarding templates
// ---------------------------------------------------------------------------

export function useOnboardingTemplates(enabled = true) {
  return useQuery({
    queryKey: hrKeys.templates(),
    queryFn: ({ signal }) => hrApi.listOnboardingTemplates(signal),
    staleTime: CATALOGUE_STALE,
    enabled,
  });
}

export function useOnboardingTemplate(id: number, enabled = true) {
  return useQuery({
    queryKey: hrKeys.template(id),
    queryFn: ({ signal }) => hrApi.getOnboardingTemplate(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

function invalidateTemplates(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: hrKeys.templates() });
}

export function useCreateOnboardingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TemplateRequest) =>
      hrApi.createOnboardingTemplate(body),
    onSuccess: () => invalidateTemplates(qc),
  });
}

export function useDeactivateOnboardingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      hrApi.deactivateOnboardingTemplate(id),
    onSuccess: (_data, id) => {
      invalidateTemplates(qc);
      void qc.invalidateQueries({ queryKey: hrKeys.template(id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Onboarding assignments + instances
// ---------------------------------------------------------------------------

export function useEmployeeOnboarding(status?: string) {
  return useQuery({
    queryKey: hrKeys.onboarding(status),
    queryFn: ({ signal }) =>
      hrApi.listEmployeeOnboarding(
        status ? { status } : undefined,
        signal,
      ),
  });
}

export function useOnboardingInstance(id: number, enabled = true) {
  return useQuery({
    queryKey: hrKeys.onboardingInstance(id),
    queryFn: ({ signal }) => hrApi.getEmployeeOnboarding(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useAssignOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignOnboardingRequest) =>
      hrApi.assignOnboarding(body),
    onSuccess: () => {
      // A new instance appears on every onboarding list scope.
      void qc.invalidateQueries({
        queryKey: [...hrKeys.all, "onboarding"],
      });
    },
  });
}

/**
 * Seed the recomputed detail query + flush the instance list (the row's
 * %-complete / status moved) after a task complete/skip. Shared by both
 * mutations — only the api call differs.
 */
function onTaskSettled(
  qc: ReturnType<typeof useQueryClient>,
  instanceId: number,
  data: OnboardingDetailResponse,
) {
  qc.setQueryData(hrKeys.onboardingInstance(instanceId), data);
  void qc.invalidateQueries({
    queryKey: [...hrKeys.all, "onboarding"],
  });
}

export function useCompleteOnboardingTask(instanceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: number; body?: TaskActionRequest }) =>
      hrApi.completeOnboardingTask(instanceId, vars.taskId, vars.body),
    onSuccess: (data) => onTaskSettled(qc, instanceId, data),
  });
}

export function useSkipOnboardingTask(instanceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: number; body?: TaskActionRequest }) =>
      hrApi.skipOnboardingTask(instanceId, vars.taskId, vars.body),
    onSuccess: (data) => onTaskSettled(qc, instanceId, data),
  });
}

/**
 * Cancel a whole onboarding instance (POST /hr/onboarding/{id}/cancel).
 * Unlike a task action, the cancel endpoint returns the leaner
 * EmployeeOnboarding (not the richer OnboardingDetailResponse with
 * tasks), so we INVALIDATE rather than seed the detail query — the
 * detail refetches with the frozen checklist + the cancelled alert. We
 * also flush the instance list (the row's status moved to cancelled).
 */
export function useCancelOnboarding(instanceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => hrApi.cancelEmployeeOnboarding(instanceId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: hrKeys.onboardingInstance(instanceId),
      });
      void qc.invalidateQueries({
        queryKey: [...hrKeys.all, "onboarding"],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// HR documents
// ---------------------------------------------------------------------------

export function useHRDocuments(userId?: number) {
  return useQuery({
    queryKey: hrKeys.documents(userId),
    queryFn: ({ signal }) =>
      hrApi.listHRDocuments(
        userId ? { user_id: userId } : undefined,
        signal,
      ),
  });
}

function invalidateDocuments(qc: ReturnType<typeof useQueryClient>) {
  // The subject param varies per view; flush every documents query.
  void qc.invalidateQueries({
    queryKey: [...hrKeys.all, "documents"],
  });
}

export function useUploadHRDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      userId: number;
      file: File;
      docType: string;
      visibility: string;
      expiresAt?: string;
    }) => hrApi.uploadHRDocument(input),
    onSuccess: () => invalidateDocuments(qc),
  });
}

export function useSignHRDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrApi.signHRDocument(id),
    onSuccess: () => invalidateDocuments(qc),
  });
}

export function useDeleteHRDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrApi.deleteHRDocument(id),
    onSuccess: () => invalidateDocuments(qc),
  });
}

// ---------------------------------------------------------------------------
// Assets + asset types
// ---------------------------------------------------------------------------

export function useAssetTypes() {
  return useQuery({
    queryKey: hrKeys.assetTypes(),
    queryFn: ({ signal }) => hrApi.listAssetTypes(signal),
    staleTime: CATALOGUE_STALE,
  });
}

function invalidateAssetTypes(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: hrKeys.assetTypes() });
}

export function useCreateAssetType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssetTypeRequest) =>
      hrApi.createAssetType(body),
    onSuccess: () => invalidateAssetTypes(qc),
  });
}

export function useUpdateAssetType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: AssetTypeRequest }) =>
      hrApi.updateAssetType(vars.id, vars.body),
    onSuccess: () => invalidateAssetTypes(qc),
  });
}

export function useAssets(status?: string, assignedTo?: number) {
  return useQuery({
    queryKey: hrKeys.assets(status, assignedTo),
    queryFn: ({ signal }) =>
      hrApi.listAssets(
        {
          ...(status ? { status } : {}),
          ...(assignedTo ? { assigned_to: assignedTo } : {}),
        },
        signal,
      ),
  });
}

/**
 * A create / transition changes the register across every status +
 * assignee filter bucket, so flush the whole assets sub-tree rather
 * than guess which buckets moved (mirrors the leave list-flush rule).
 */
function invalidateAssets(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: [...hrKeys.all, "assets"] });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAssetRequest) => hrApi.createAsset(body),
    onSuccess: () => invalidateAssets(qc),
  });
}

export function useAssignAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: AssignAssetRequest }) =>
      hrApi.assignAsset(vars.id, vars.body),
    onSuccess: () => invalidateAssets(qc),
  });
}

export function useReturnAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrApi.returnAsset(id),
    onSuccess: () => invalidateAssets(qc),
  });
}

export function useRetireAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrApi.retireAsset(id),
    onSuccess: () => invalidateAssets(qc),
  });
}

export function useMarkAssetLost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrApi.markAssetLost(id),
    onSuccess: () => invalidateAssets(qc),
  });
}
